import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, LayoutGrid, Search } from 'lucide-react';
import { collectionsApi } from '@/api/collectionsApi';
import { categoriesApi } from '@/api/categoriesApi';
import type { ProductFormState } from './types';

interface StepCollectionProps {
  state: ProductFormState;
  updateState: (updater: ProductFormState | ((prev: ProductFormState) => ProductFormState)) => void;
}

export function StepCollection({ state, updateState }: StepCollectionProps) {
  const [categorySearch, setCategorySearch] = useState('');

  const { data: collections } = useQuery({
    queryKey: ['collections'],
    queryFn: () => collectionsApi.getAll(false),
  });

  const { data: categories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesApi.getAll(false),
  });

  const toggleCategory = (categoryId: string) => {
    updateState((prev) => {
      const next = prev.categoryIds.includes(categoryId)
        ? prev.categoryIds.filter((id) => id !== categoryId)
        : [...prev.categoryIds, categoryId];
      const firstCat = categories?.find((c) => c.id === next[0]);
      return {
        ...prev,
        categoryIds: next,
        category: firstCat?.name || prev.category,
      };
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-3">
          Collection <span className="text-gray-600 dark:text-warm-sand/50 text-xs font-normal">(optional)</span>
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => updateState((prev) => ({ ...prev, collectionId: null }))}
            className={`
              flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all border-2
              ${
                !state.collectionId
                  ? 'bg-amber-450/10 border-amber-450'
                  : 'bg-white dark:bg-charcoal-800 border-gray-200 dark:border-charcoal-700'
              }
            `}
          >
            <span className="text-xl">—</span>
            <span className="text-sm font-medium text-gray-900 dark:text-warm-cream">No collection</span>
          </button>
          {collections?.map((collection) => {
            const isSelected = state.collectionId === collection.id;
            return (
              <button
                key={collection.id}
                type="button"
                onClick={() =>
                  updateState((prev) => ({ ...prev, collectionId: collection.id }))
                }
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all border-2
                  ${
                    isSelected
                      ? 'bg-amber-450/10 border-amber-450 shadow-md'
                      : 'bg-white dark:bg-charcoal-800 border-gray-200 dark:border-charcoal-700'
                  }
                `}
              >
                <span className="text-2xl">{collection.icon || '📁'}</span>
                <div className="min-w-0">
                  <p className={`text-sm font-medium ${isSelected ? 'text-amber-450' : ''}`}>
                    {collection.name}
                  </p>
                  {collection.description && (
                    <p className="text-xs text-gray-600 dark:text-warm-sand/70 truncate">
                      {collection.description}
                    </p>
                  )}
                </div>
                {isSelected && (
                  <CheckCircle2 className="w-4 h-4 text-amber-450 ml-auto flex-shrink-0" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
          Product Design / Type
        </label>
        <input
          type="text"
          value={state.productDesign}
          onChange={(e) =>
            updateState((prev) => ({ ...prev, productDesign: e.target.value }))
          }
          placeholder="Duck w (5) Ducklings, Bone, Floral Heart..."
          className="w-full px-4 py-3 bg-white dark:bg-charcoal-800 border border-gray-300 dark:border-charcoal-600 rounded-xl text-gray-900 dark:text-warm-cream focus:outline-none focus:border-amber-450"
        />
        <p className="mt-1 text-xs text-gray-600 dark:text-warm-sand/60">
          The design this item is based on.
        </p>
      </div>

      <div>
        <label className="flex items-center gap-2 text-sm font-medium text-gray-900 dark:text-warm-cream mb-2">
          <LayoutGrid className="w-4 h-4 text-amber-450" />
          Design Family Categories
          {state.categoryIds.length > 0 && (
            <span className="ml-auto text-xs text-amber-450 font-normal">
              {state.categoryIds.length} selected
            </span>
          )}
        </label>
        {!categories?.length ? (
          <p className="text-xs text-gray-600 dark:text-warm-sand/60 italic">
            No categories available.
          </p>
        ) : (
          <div className="p-3 bg-white dark:bg-charcoal-800/50 border border-gray-200 dark:border-charcoal-700 rounded-xl">
            {state.categoryIds.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                {state.categoryIds.map((id) => {
                  const cat = categories.find((c) => c.id === id);
                  if (!cat) return null;
                  return (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                      style={{
                        backgroundColor: cat.color ? `${cat.color}22` : '#f59e0b22',
                        color: cat.color || '#f59e0b',
                        border: `1px solid ${cat.color || '#f59e0b'}55`,
                      }}
                    >
                      {cat.name}
                      <button type="button" onClick={() => toggleCategory(id)} className="hover:opacity-70">
                        ×
                      </button>
                    </span>
                  );
                })}
              </div>
            )}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-taupe" />
              <input
                type="text"
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
                placeholder="Filter categories..."
                className="w-full pl-9 pr-4 py-2 bg-gray-100 dark:bg-charcoal-700 border border-gray-200 dark:border-charcoal-600 rounded-lg text-sm text-gray-900 dark:text-warm-cream focus:outline-none focus:border-amber-450"
              />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-40 overflow-y-auto">
              {categories
                .filter(
                  (c) =>
                    c.isActive &&
                    c.name.toLowerCase().includes(categorySearch.toLowerCase())
                )
                .map((cat) => {
                  const isSelected = state.categoryIds.includes(cat.id);
                  return (
                    <label
                      key={cat.id}
                      className={`flex items-center gap-2 px-2.5 py-2 rounded-lg cursor-pointer border text-sm ${
                        isSelected
                          ? 'border-amber-450/50 bg-amber-450/10 text-amber-600 dark:text-amber-400'
                          : 'border-gray-200 dark:border-charcoal-600 bg-gray-50 dark:bg-charcoal-700/50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleCategory(cat.id)}
                        className="rounded border-gray-300 text-amber-450 focus:ring-amber-450"
                      />
                      <span className="truncate">{cat.name}</span>
                    </label>
                  );
                })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
